// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import VideoAsset, { VideoAssetsStyles } from "@conklin/App/Components/Media/VideoAsset";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React from "react";

const enum fields {
    videoType = "videoType",
    videoURL = "videoURL",
    videoParameters = "videoParameters",
    title = "title",
    description = "description",
    uploadDate = "uploadDate",
    duration = "duration",
}

export interface VideosStyles {
    video?: VideoAssetsStyles;
}

interface YoutubeVideoProps extends WidgetProps {
    fields: {
        [fields.videoType]: "YouTube" | "Vimeo" | "StandardVideo";
        [fields.videoURL]: string;
        [fields.videoParameters]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.uploadDate]: string;
        [fields.duration]: string;
    };
}

export const videosStyles: VideosStyles = {};

const styles = videosStyles;

const videoTemplateWidget = ({
    fields: { videoType, videoURL, videoParameters, title, description, uploadDate, duration },
}: YoutubeVideoProps) => {
    return (
        <VideoAsset
            name={title}
            type={videoType}
            url={videoURL}
            videoParameters={videoParameters}
            description={description}
            uploadDate={uploadDate}
            duration={duration}
            extendedStyles={styles.video}
        />
    );
};

const metadataMsg = "will be used to add metadata for this video.";

export const definition: WidgetDefinition = {
    group: XNGAGE_CUSTOM_WIDGET,
    fieldDefinitions: [
        {
            name: fields.videoType,
            displayName: "Video Type",
            editorTemplate: "DropDownField",
            defaultValue: "YouTube",
            options: [
                {
                    displayName: "Youtube",
                    value: "YouTube",
                },
                {
                    displayName: "Vimeo",
                    value: "Vimeo",
                },
                {
                    displayName: "MP4",
                    value: "StandardVideo",
                },
            ],
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.videoURL,
            displayName: "Video URL or Id",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.videoParameters,
            displayName: "Video Parameters",
            editorTemplate: "TextField",
            defaultValue: "",
            tooltip: `e.g: "start=1150&other=value"`,
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.uploadDate,
            displayName: "Upload Date",
            editorTemplate: "DateTimeField",
            defaultValue: new Date(),
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
        {
            name: fields.duration,
            displayName: "Duration",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
            tooltip: metadataMsg,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: videoTemplateWidget,
    definition,
};

export default widgetModule;
